import { isOneOf } from '@shared/helpers/array.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  ElementPropertyName,
  ElementPropertyType,
  IElementProperty,
  IFactoryExpressionProperty,
  IFactoryProperty,
} from '@shared/models/element-property.model';
import {
  SbefElementProperty,
  LifetimeElementProperty,
  CommonElementCategoryProperty,
  CommonElementCategoryPropertyName,
  commonElementCategoryPropertyNames,
  systemElementCategoryPropertyNames,
  SystemElementCategoryProperty,
  SystemElementCategoryPropertyName,
} from './common-element-category-properties.model';
import { sbefCodesList } from '@shared/construction/sbef-codes.data';
import { lazyCacheGenerate } from '@shared/helpers/function.helpers';
import {
  getSbefPropertySortKey,
  getSbefSelectorLabel,
  isSbefPropertySelectorCode,
} from '@shared/helpers/sbef.helpers';

/**
 * Test if a property is a sbef property (of type select)
 * @param prop
 * @returns
 */
export const isElementSbefProperty = (
  prop: IElementProperty | undefined,
): prop is SbefElementProperty => prop?.name === ElementPropertyName.SbefCode;

/**
 * Test if a property is a lifetime property (of type expression)
 * @param prop
 * @returns
 */
export const isElementLifetimeProperty = (
  prop: IElementProperty | undefined,
): prop is LifetimeElementProperty =>
  prop?.name === ElementPropertyName.Lifetime;

/**
 * Test if a property is a common element category property
 * @param prop
 * @returns
 */
export const isCommonElementCategoryProperty = (
  prop: IElementProperty | undefined,
): prop is CommonElementCategoryProperty =>
  guards.is.object(prop) && isCommonElementCategoryPropertyName(prop.name);

/**
 * Test if a value is a common element category property name like sbef_code or lifetime
 * @param value
 * @returns
 */
export const isCommonElementCategoryPropertyName = (
  value: unknown,
): value is CommonElementCategoryPropertyName =>
  isOneOf(commonElementCategoryPropertyNames, value);

/**
 * Test if a property is a system element category property
 * @param prop
 * @returns
 */
export const isSystemElementCategoryProperty = (
  prop: IElementProperty | undefined,
): prop is SystemElementCategoryProperty =>
  guards.is.object(prop) && isSystemElementCategoryPropertyName(prop.name);

/**
 * Test if a value is a system element category property name like content_coverage
 * @param value
 * @returns
 */
export const isSystemElementCategoryPropertyName = (
  value: unknown,
): value is SystemElementCategoryPropertyName =>
  isOneOf(systemElementCategoryPropertyNames, value);

/**
 * Whether a property name is {@link ElementPropertyName.ContentCoverage}.
 */
export const isContentCoveragePropertyName = (
  name: unknown,
): name is ElementPropertyName.ContentCoverage =>
  name === ElementPropertyName.ContentCoverage;

/**
 * Detect content-coverage factory payloads when `name` was dropped (e.g. partial change payloads).
 * Shape-only match also requires `hidden: true` (template default) to avoid other %-bounded properties.
 */
export const isContentCoverageFactoryShape = (
  factory: Partial<IFactoryProperty>,
): boolean => {
  if (isContentCoveragePropertyName(factory.name)) return true;
  if (!('unit' in factory) || factory.hidden !== true) return false;
  const expressionFactory = factory as Partial<IFactoryExpressionProperty>;
  return (
    expressionFactory.unit === '%' &&
    expressionFactory.min === 0.01 &&
    expressionFactory.max === 1
  );
};

/**
 * Factory defaults for adding content coverage (always expression, never inherited).
 */
type ContentCoverageFactoryOverrides = Pick<
  IFactoryExpressionProperty,
  'id' | 'optional' | 'hidden' | 'count' | 'fallbackCount'
>;

/**
 * Safe overrides when normalizing a mixed {@link IFactoryProperty} to content coverage.
 */
export const contentCoverageFactoryOverridesFrom = (
  factory: IFactoryProperty,
): ContentCoverageFactoryOverrides => ({
  optional: factory.optional,
  hidden: factory.hidden,
  // Id has to survive normalization so recipe apply can reuse the property.
  ...(guards.is.string(factory.id) ? { id: factory.id } : {}),
  ...(factory.type === ElementPropertyType.Expression ||
  factory.type === undefined
    ? {
        count: factory.count as ContentCoverageFactoryOverrides['count'],
        fallbackCount:
          factory.fallbackCount as ContentCoverageFactoryOverrides['fallbackCount'],
      }
    : {}),
});

export const toContentCoverageFactory = (
  overrides: ContentCoverageFactoryOverrides = {},
): IFactoryExpressionProperty => {
  return {
    ...overrides,
    name: ElementPropertyName.ContentCoverage,
    type: ElementPropertyType.Expression,
    unit: '%',
    min: 0.01,
    max: 1,
    fallbackCount: overrides.fallbackCount ?? 1,
    hidden: overrides.hidden ?? true,
    inheritFallback: false,
  };
};

/**
 * Options for the SBEF code property (to show in the menu).
 * Note that the "auto" value is not included here since that is not selectable as data
 * This was previously calculated at import time but it created circular dependency issues.
 *
 * Also, this list must be sorted! it will be rendered as is!
 */
export const getSbefPropertyOptions = lazyCacheGenerate(() => {
  return Object.freeze(
    sbefCodesList
      .filter((code) => isSbefPropertySelectorCode(code))
      .map((code) => ({
        value: code,
        label: getSbefSelectorLabel(code, { includeDescription: true }),
      }))
      .sort(
        (a, b) =>
          getSbefPropertySortKey(a.value) - getSbefPropertySortKey(b.value),
      ),
  );
});
